import { alpha } from '@mui/material/styles';
import React, { useState } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { AddressInput, PointmanCustomerCreateDTO, PointmanCustomerUpdateDTO } from '_lib/api/apiTypes.shim';
import { CheckboxFormItem } from '_lib/components/CheckboxFormItem/CheckboxFormItem';
import { FieldSetProps } from '_lib/components/FieldSet/FieldSet';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { FormControlLabel } from '_lib/components/FormControlLabel/FormControlLabel';
import { Grid } from '_lib/components/Grid/Grid';
import { GridV2 } from '_lib/components/GridV2/GridV2';
import { Radio } from '_lib/components/Radio/Radio';
import { RadioGroup } from '_lib/components/RadioGroup/RadioGroup';
import { Stack } from '_lib/components/Stack/Stack';
import { PhoneNumberTextFieldFormItem, TextFieldFormItem } from '_lib/components/TextFieldFormItem/TextFieldFormItem';
import { Typography, TypographyProps } from '_lib/components/Typography/Typography';
import { BaseAddressForm } from '_lib/featureComponents/FormItems/BaseAddressForm';
import { toSxArray } from '_lib/utils/helpers';

const StyledFieldSet = (
  props: Pick<FieldSetProps, 'children' | 'legendText' | 'sx'> & { legendVariant?: TypographyProps['variant'] }
) => {
  return (
    <FlexBox
      sx={[
        (theme) => ({
          width: '100%',
          borderWidth: 0,
          backgroundColor: theme.palette.grey[100],
          border: `1px solid ${alpha(theme.palette.common.black, 0.13)}`,
          borderRadius: '3px'
        }),
        ...toSxArray(props.sx)
      ]}
      component={'fieldset'}>
      <legend style={{ paddingInline: 0 }}>
        <Stack
          direction='row'
          sx={(theme) => ({
            alignItems: 'center',
            width: '100%',
            height: '100%',
            position: 'relative',
            borderTop: `1px solid ${alpha(theme.palette.common.black, 0.13)}`,
            borderRadius: '3px',
            backgroundColor: theme.palette.grey[100],
            paddingX: 1.5,
            paddingTop: 0.25,
            '::before': {
              content: `""`,
              width: '2px',
              borderLeft: `1px solid ${alpha(theme.palette.common.black, 0.13)}`,
              borderRadius: '3px',
              height: '50%',
              top: 0,
              left: 0,
              position: 'absolute'
            },
            '::after': {
              content: `""`,
              width: '2px',
              borderRight: `1px solid ${alpha(theme.palette.common.black, 0.13)}`,
              borderRadius: '3px',
              height: '50%',
              top: 0,
              right: 0,
              position: 'absolute'
            }
          })}
          spacing={1}>
          <Typography variant={props.legendVariant || 'subtitle1'} text={props.legendText} />
        </Stack>
      </legend>
      {props.children}
    </FlexBox>
  );
};

export type CustomerFormBaseValues = Pick<
  PointmanCustomerUpdateDTO,
  | 'isCommercial'
  | 'uniqueId'
  | 'companyName'
  | 'firstName'
  | 'lastName'
  | 'phoneNumber'
  | 'addressLine1'
  | 'addressLine2'
  | 'city'
  | 'stateOrProvince'
  | 'zipOrPostal'
  | 'country'
  | 'email'
  | 'secondaryContact'
  | 'leadSourceIndicator'
  | 'notes'
  | 'isOwner'
  | 'billingContact'
>;

// Union type for form values
export type CustomerFormValues = CustomerFormBaseValues;

// Updated props to accept either create or update form context
type CustomerFormProps = {
  customerFormContext: UseFormReturn<PointmanCustomerCreateDTO> | UseFormReturn<PointmanCustomerUpdateDTO>;
  isEditingExisting?: boolean;
};

export const CustomerForm = (props: CustomerFormProps) => {
  const { customerFormContext, isEditingExisting } = props;

  const { control, getValues, setValue, watch } =
    customerFormContext as unknown as UseFormReturn<CustomerFormBaseValues>;

  const [nameCache, setNameCache] = useState({
    firstName: getValues('firstName'),
    lastName: getValues('lastName'),
    companyName: getValues('companyName')
  });

  const customerIsCommercialChangeHandler = (isCommercial: boolean) => {
    const formValues = getValues();
    const { firstName: cacheFirstName, lastName: cacheLastName, companyName: cacheCompanyName } = nameCache;

    // Update name cache containing current form values before resetting form
    setNameCache({
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      companyName: formValues.companyName
    });

    setValue('isCommercial', isCommercial);
    if (isCommercial) {
      setValue('firstName', null);
      setValue('lastName', null);
      setValue('companyName', cacheCompanyName || '');
    } else {
      setValue('firstName', cacheFirstName || '');
      setValue('lastName', cacheLastName || '');
      setValue('companyName', null);
    }
  };

  return (
    <GridV2 container spacing={1} sx={(theme) => ({ backgroundColor: theme.palette.background.default, padding: 1.5 })}>
      <GridV2 size={12}>
        <RadioGroup
          name={'isCommercial'}
          row={true}
          onChange={(e) => customerIsCommercialChangeHandler(e.target.value === 'true')}>
          <FormControlLabel
            checked={watch('isCommercial') === false}
            value={false}
            control={<Radio />}
            label='Residential'
          />
          <FormControlLabel
            checked={watch('isCommercial') === true}
            value={true}
            control={<Radio />}
            label='Commercial'
          />
        </RadioGroup>
      </GridV2>
      <GridV2 size={12}>
        <TextFieldFormItem name='uniqueId' label='ID' control={control} />
      </GridV2>
      <GridV2 size={12}>
        <TextFieldFormItem
          name='companyName'
          label='Company Name'
          control={control}
          required={watch('isCommercial') === true}
        />
      </GridV2>
      <GridV2 size={{ xs: 12, md: 6 }}>
        <TextFieldFormItem
          name='firstName'
          label='First Name'
          control={control}
          required={watch('isCommercial') === false}
        />
      </GridV2>
      <GridV2 size={{ xs: 12, md: 6 }}>
        <TextFieldFormItem
          name='lastName'
          label='Last Name'
          control={control}
          required={watch('isCommercial') === false}
        />
      </GridV2>
      <GridV2 size={12}>
        <PhoneNumberTextFieldFormItem name='phoneNumber' label='Phone Number' control={control} />
      </GridV2>
      <GridV2 size={12}>
        <Grid container spacing={1}>
          <BaseAddressForm
            formContext={customerFormContext}
            isNested={false}
            isEditingExisting={isEditingExisting || false}
          />
        </Grid>
      </GridV2>
      <GridV2 size={12}>
        <TextFieldFormItem name='email' label='Email' control={control} />
      </GridV2>
      <StyledFieldSet legendText='Secondary Contact' sx={{ marginY: 0.5 }}>
        <Stack padding={0.5} spacing={1} useFlexGap sx={{ width: '100%' }}>
          <TextFieldFormItem name='secondaryContact.name' label='First & Last Name' control={control} />
          <PhoneNumberTextFieldFormItem name='secondaryContact.phoneNumber' label='Phone Number' control={control} />
        </Stack>
      </StyledFieldSet>
      <GridV2 size={12}>
        <TextFieldFormItem name='leadSourceIndicator' label='Lead Source Indicator' control={control} />
      </GridV2>
      <GridV2 size={12}>
        <TextFieldFormItem name='notes' label='Notes' multiline control={control} />
      </GridV2>
      <CheckboxFormItem name='isOwner' label='Property Owner' control={control} />
      {watch('isOwner') === false && (
        <StyledFieldSet legendText='Billing Contact'>
          <GridV2 container spacing={1} sx={(theme) => ({ backgroundColor: theme.palette.grey[100], padding: 0.5 })}>
            <GridV2 size={12}>
              <TextFieldFormItem name='billingContact.companyName' label='Company Name' control={control} />
            </GridV2>
            <GridV2 size={{ xs: 12, md: 6 }}>
              <TextFieldFormItem name='billingContact.firstName' label='First Name' control={control} />
            </GridV2>
            <GridV2 size={{ xs: 12, md: 6 }}>
              <TextFieldFormItem name='billingContact.lastName' label='Last Name' control={control} />
            </GridV2>
            <GridV2 size={12}>
              <PhoneNumberTextFieldFormItem name='billingContact.phoneNumber' label='Phone Number' control={control} />
            </GridV2>
            <GridV2 size={12}>
              <Grid container spacing={1}>
                <BaseAddressForm
                  formContext={customerFormContext as unknown as UseFormReturn<{ billingContact: AddressInput }>}
                  isNested
                  nestedPath='billingContact'
                  isEditingExisting={isEditingExisting || false}
                  optional
                />
              </Grid>
            </GridV2>
            <GridV2 size={12}>
              <TextFieldFormItem name='billingContact.email' label='Email' control={control} />
            </GridV2>
          </GridV2>
        </StyledFieldSet>
      )}
    </GridV2>
  );
};
